import React, { FC, useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import {
  enrollProgramValidationSchema,
  EnrollProgramFormData,
} from "../../../schemas/participants/validation.schema";
import { RoleStatusEnum } from "@/domain/role.enum";
import Grid from "@mui/material/Grid2";
import { TextField, Skeleton } from "@mui/material";
import Select from "@/components/Select/index";
import { useGetProgramOrgUnits } from "@/hooks/useParticipants";
import { useParams } from "react-router-dom";
import { useGetCmCoordinator } from "@/hooks/useUsers";
import { useCreateEnrollment } from "@/hooks/useEnrollments";
import { useGetProgram } from "@/hooks/management/usePrograms.manage";
import { AssignCMCoordinatorEnum } from "@/domain/programs.enum";
import { AlertDialog } from "@/components/AlertDialog/AlertDialog";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";
import { ParticipantResponse } from "@/domain/participant.interface";

type EnrollProgramModalProps = {
  open: boolean;
  onClose: () => void;
  participantResponse?: ParticipantResponse;
};

const EnrollProgramModal: FC<EnrollProgramModalProps> = ({
  open,
  onClose,
  participantResponse,
}) => {
  const { t } = useTranslation("clients");
  const { t: tCommon } = useTranslation("common");
  const { userInfo, userHasAccessToCode } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const theme = useAppTheme();
  const { participantId } = useParams();

  const createAuditLog = useCreateAuditlog();
  const createEnrollment = useCreateEnrollment();

  const [errorModal, setErrorModal] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  // AC00-B: Check permissions
  const canAddEnrollment = userHasAccessToCode("PARTICIPANT_ADD_ENROLLMENT");

  // AC01a/AC02a: Load programs according to user role
  const { programOrgUnits, isFetchingProgramOrgUnits } = useGetProgramOrgUnits(
    participantId || "",
    userInfo?._id || "",
    false, // editing = false for ADD mode
  );

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    trigger,
  } = useForm<EnrollProgramFormData>({
    resolver: yupResolver(
      enrollProgramValidationSchema(tCommon, undefined, undefined),
    ),
    mode: "all",
    defaultValues: {
      name: "",
      status: RoleStatusEnum.ACTIVE,
      actions: [],
    },
  });

  const programWatched = watch("program") as string;
  const organizationUnitWatched = watch("organizationUnit") as string;
  const formFactor1Watched = watch("formFactor1") as string;

  const { data: program, isFetchingProgram } = useGetProgram(
    programWatched || "",
  );

  // AC00-A: Audit log when opening modal
  useEffect(() => {
    if (open && canAddEnrollment) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ENROLLMENTS,
        option: OPTIONS.ENROLLMENT,
        actionCode: ACTION_CODE.WEB_PART_ENROLL_ADD_ENTER,
        action: "When entering to add",
        detail: "User has entered the dialog to add enrollment",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id || "",
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
    }
  }, [open]);

  // Validate participant state when modal opens
  useEffect(() => {
    if (open && participantResponse?.participant && canAddEnrollment) {
      const participant = participantResponse.participant;
      // Validate the participant state
      if (participant?.hasMultipleActiveEnrollments) {
        onClose();
        showSnackBar(
          "Sorry, a participant can only have one active enrolled program.",
          "error",
        );
      } else if (participant?.reachedMaximumYearlyEarnings) {
        onClose();
        showSnackBar(
          "The participant cannot be enrolled to a new program because the maximum yearly incentive earnings amount has been reached. The participant will be able to join a new program on January 1st of next year.",
          "error",
        );
      }
    }
  }, [open, participantResponse, canAddEnrollment, onClose, showSnackBar]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset();
      setErrorModal({ open: false, message: "" });
    }
  }, [open, reset]);

  const assignCMCoordinator = useMemo(
    () => (program?.configuration as any)?.assignCMCoordinator,
    [program],
  );

  useEffect(() => {
    if (!programWatched) {
      setValue("organizationUnit", "");
      setValue("cmCoordinator", "");
      setValue("formFactor1", "");
      setValue("formFactor2", "");
      setValue("naloxoneId", "");
    }
  }, [programWatched, setValue]);

  // Clear formFactor2 if it matches the newly selected formFactor1
  useEffect(() => {
    const formFactor2Value = getValues("formFactor2") as string;
    if (formFactor1Watched && formFactor2Value === formFactor1Watched) {
      setValue("formFactor2", "");
    }
  }, [formFactor1Watched, setValue, getValues]);

  // AC01a/AC02a: Select first program by default (alphabetically sorted)
  useEffect(() => {
    if (
      programOrgUnits &&
      programOrgUnits.length > 0 &&
      !programWatched &&
      open
    ) {
      const firstProgram = programOrgUnits[0];
      setValue("program", firstProgram.programId);
    }
  }, [programOrgUnits, programWatched, open, setValue]);

  const showCMCoordinator = useMemo(
    () =>
      assignCMCoordinator === AssignCMCoordinatorEnum.OPTIONAL ||
      assignCMCoordinator === AssignCMCoordinatorEnum.MANDATORY,
    [assignCMCoordinator],
  );

  const isCMCoordinatorRequired = useMemo(
    () => assignCMCoordinator === AssignCMCoordinatorEnum.MANDATORY,
    [assignCMCoordinator],
  );

  // AC04a: Check if Form Factor 2 should be shown
  const enabledFormFactor2 = useMemo(
    () => program?.configuration?.enabledFormFactor2,
    [program],
  );

  // AC10: Get form factors from program
  const formFactors = useMemo(
    () => program?.configuration?.formFactors || [],
    [program],
  );

  const formFactorOptions = useMemo(() => {
    return formFactors.map((factor) => ({
      key: factor.type,
      value: factor.name,
    }));
  }, [formFactors]);

  // Form Factor 1 options: all available options
  const formFactor1Options = useMemo(() => {
    return formFactorOptions;
  }, [formFactorOptions]);

  // Form Factor 2 options: exclude the one selected in Form Factor 1
  const formFactor2Options = useMemo(() => {
    if (!formFactor1Watched) return formFactorOptions;
    return formFactorOptions.filter(
      (option) => option.key !== formFactor1Watched,
    );
  }, [formFactorOptions, formFactor1Watched]);

  useEffect(() => {
    setValue("assignCMCoordinator", assignCMCoordinator || "");
    setValue("enabledFormFactor2", enabledFormFactor2 || false);
  }, [program, assignCMCoordinator, enabledFormFactor2, setValue]);

  // AC03a: Load CM Coordinators (already filtered by backend to exclude Sysadmin and Support)
  const { cmCoordinatorUsers, isFetchingCmCoordinatorUsers } =
    useGetCmCoordinator(
      {
        programId: programWatched,
        orgUnitId: organizationUnitWatched,
      },
      !!programWatched &&
        !!organizationUnitWatched &&
        showCMCoordinator === true,
    );

  // AC01a/AC02a: Programs are already sorted alphabetically by backend
  const programs = useMemo(() => {
    if (!programOrgUnits) return [];

    return programOrgUnits
      ?.map((programOrgUnit) => {
        return {
          key: programOrgUnit.programId,
          value: programOrgUnit.programName,
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value)); // Ensure alphabetical order
  }, [programOrgUnits]);

  // AC01a/AC02a: Organization Units are filtered by ACTIVE status by backend
  const organizationUnits = useMemo(() => {
    if (!programOrgUnits || !programWatched) return [];

    return (
      programOrgUnits.find((it) => it.programId == programWatched)?.orgUnits ||
      []
    )
      .map((it) => {
        return {
          key: it.orgUnitId,
          value: it.orgUnitName,
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value)); // Ensure alphabetical order
  }, [programOrgUnits, programWatched]);

  const cmCoordinatorUserOptions = useMemo(() => {
    if (!cmCoordinatorUsers) return [];
    return cmCoordinatorUsers.map((it) => ({
      key: it._id as string,
      value: `${it.firstName} ${it.lastName}`,
    }));
  }, [cmCoordinatorUsers, programWatched, organizationUnitWatched]);

  const onSubmit = async (data: EnrollProgramFormData) => {
    // Manual validation for CM Coordinator when mandatory
    if (isCMCoordinatorRequired && !data.cmCoordinator) {
      showSnackBar(tCommon("validations.required"), "error");
      return;
    }

    const payload = {
      participantId: participantId as string,
      programId: data.program as string,
      orgUnitId: data.organizationUnit as string,
      cmCoordinator: (data.cmCoordinator as string) || null,
      formFactor1: (data.formFactor1 as string) || null,
      formFactor2: (data.formFactor2 as string) || null,
      naloxoneId: (data.naloxoneId as string) || null,
      createdBy: userInfo?._id as string,
    };

    await createEnrollment.mutateAsync(payload, {
      onSuccess: () => {
        onClose();
        showSnackBar(t("client_profile.enroll-success"), "success");

        const participantName = `${participantResponse?.participant.firstName} ${participantResponse?.participant.lastName}`;
        // AC00-A: Audit log when enrollment is added
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ENROLLMENTS,
          option: OPTIONS.ENROLLMENT,
          actionCode: ACTION_CODE.WEB_PART_ENROLL_ADDED,
          action: "When saved",
          detail: `User has added enrollment to participant ${participantName}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      },
      onError: (error: ErrorResponse) => {
        const message = Array.isArray(error.message)
          ? error.message[0]
          : error.message;

        // AC06a: Max participants exceeded
        if (message?.includes("maximum number of participants")) {
          setErrorModal({
            open: true,
            message: t("client_profile.max-participants-reached"),
          });
          return;
        }

        // AC07a: Cannot enroll if another enroll is active
        if (message?.includes("already enrolled")) {
          setErrorModal({
            open: true,
            message: t("client_profile.already-enrolled"),
          });
          return;
        }

        showSnackBar(message || t("client_profile.enroll-error"), "error");
      },
    });
  };

  // AC00-B: Show PermissionDeniedModal if no permission
  if (!canAddEnrollment) {
    return <PermissionDeniedModal open={open} onClose={onClose} />;
  }

  // AC00-D: Show skeletons while loading
  const isLoading = isFetchingProgramOrgUnits || isFetchingProgram;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        isPending={false}
        title={t("client_profile.enroll_to_new_program")}
        theme={theme}
        size={{
          width: "100%",
          minHeight: 440,
          maxHeight: 900,
          maxWidth: 320,
        }}
        content={
          isLoading ? (
            <Grid container spacing={2} mt={2}>
              <Grid size={{ xs: 12 }}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2} mt={2}>
              {/* Program */}
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="program"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      isLoading={isFetchingProgramOrgUnits}
                      data={programs}
                      value={(field.value as string) || ""}
                      label={t("client_profile.program")}
                      id="program"
                      fullWidth
                      error={!!errors.program}
                      errorMessage={errors.program?.message}
                      required={true}
                    />
                  )}
                />
              </Grid>
              {/* Org Unit */}
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="organizationUnit"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      isLoading={isFetchingProgramOrgUnits}
                      disabled={!programWatched}
                      data={organizationUnits}
                      value={(field.value as string) || ""}
                      label={t("client_profile.organization_unit")}
                      id="organizationUnit"
                      fullWidth
                      error={!!errors.organizationUnit}
                      errorMessage={errors.organizationUnit?.message}
                      required={true}
                    />
                  )}
                />
              </Grid>
              {/* AC03a: CM Coordinator - conditional based on assignCMCoordinator */}
              {showCMCoordinator && (
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="cmCoordinator"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        handleChange={(value) => field.onChange(value)}
                        isLoading={isFetchingCmCoordinatorUsers}
                        disabled={!programWatched || !organizationUnitWatched}
                        data={cmCoordinatorUserOptions}
                        value={(field.value as string) || ""}
                        label={t("client_profile.cm_coordinator")}
                        id="cmCoordinator"
                        fullWidth
                        error={!!errors.cmCoordinator}
                        errorMessage={errors.cmCoordinator?.message}
                        required={isCMCoordinatorRequired}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* AC10: Form Factor 1 */}
              {formFactor1Options.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="formFactor1"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        handleChange={(value) => field.onChange(value)}
                        isLoading={false}
                        disabled={!programWatched}
                        data={formFactor1Options}
                        value={(field.value as string) || ""}
                        label="Form factor 1"
                        id="formFactor1"
                        fullWidth
                        error={!!errors.formFactor1}
                        errorMessage={errors.formFactor1?.message}
                        required={false}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* AC04a: Form Factor 2 - conditional based on enabledFormFactor2 */}
              {enabledFormFactor2 && formFactor2Options.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="formFactor2"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        handleChange={(value) => field.onChange(value)}
                        isLoading={false}
                        disabled={!programWatched}
                        data={formFactor2Options}
                        value={(field.value as string) || ""}
                        label="Form factor 2"
                        id="formFactor2"
                        fullWidth
                        error={!!errors.formFactor2}
                        errorMessage={errors.formFactor2?.message}
                        required={false}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* AC11: Naloxone ID */}
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="naloxoneId"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Naloxone ID"
                      id="naloxoneId"
                      fullWidth
                      error={!!errors.naloxoneId}
                      helperText={errors.naloxoneId?.message}
                      inputProps={{
                        maxLength: 20,
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )
        }
        actions={
          <Box
            sx={{
              display: "flex",
              gap: "10px",
            }}
          >
            <Button
              variantType="cancel"
              onClick={onClose}
              disabled={false}
              label={tCommon("cancel")}
              sx={{ textTransform: "uppercase" }}
            />
            <Button
              variantType="save"
              onClick={async () => {
                const isValid = await trigger();
                if (isValid) {
                  handleSubmit(onSubmit)();
                } else {
                  showSnackBar(tCommon("validations.form-with-errors"));
                }
              }}
              disabled={createEnrollment.isPending}
              isLoading={createEnrollment.isPending}
              label={t("client_profile.enroll")}
              sx={{ textTransform: "uppercase" }}
            />
          </Box>
        }
      />

      {errorModal.open && (
        <AlertDialog
          open={errorModal.open}
          title=""
          description={errorModal.message}
          onCancel={() => setErrorModal({ open: false, message: "" })}
          onConfirm={() => setErrorModal({ open: false, message: "" })}
          confirmText={tCommon("accept")}
          cancelText={tCommon("cancel")}
        />
      )}
    </>
  );
};

export default EnrollProgramModal;
